/**
 * Utilitaires i18n partagés pour les fiches et libellés d'intégration (LOT G, G12).
 *
 * Historiquement, la sélection d'un texte localisé (`{ fr, en, … }` → string)
 * était recopiée dans ~5 composants avec des ordres de repli divergents (dont
 * une version FR-first buggée qui ignorait la locale courante). On centralise
 * ici l'unique implémentation, ordre de repli canonique :
 *
 *   locale courante → en → fr → première valeur → ''
 *
 * De même, le parsing du champ `category_name` (JSON stringifié `{ fr, en }`,
 * ou déjà objet) était copié 3 fois — un seul parseur ici.
 */

export type LocalizedText = Record<string, string | undefined>;

/**
 * Sélectionne le texte pour la `locale` demandée avec repli canonique.
 *
 * - `value` string → renvoyé tel quel (texte legacy non localisé).
 * - `value` objet `{ fr, en, … }` → `locale` → `en` → `fr` → 1re valeur → ''.
 * - `value` nul/vide → ''.
 */
export function pickLocalizedText(
  value: string | LocalizedText | null | undefined,
  locale: string,
): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  const loc = String(locale || 'fr');
  return (
    value[loc]
    || value.en
    || value.fr
    || Object.values(value).find((v) => typeof v === 'string' && v !== '')
    || ''
  );
}

/**
 * Parse un `category_name` renvoyé par l'API — soit une chaîne JSON
 * `'{"fr":"…","en":"…"}'`, soit déjà un objet `{ fr, en }`. Renvoie l'objet
 * localisé, ou `null` si le contenu est illisible (l'appelant décide du repli).
 */
export function parseCategoryName(
  raw: string | LocalizedText | null | undefined,
): LocalizedText | null {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw as LocalizedText;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as LocalizedText) : null;
  } catch {
    return null;
  }
}

/**
 * Raccourci : parse un `category_name` PUIS sélectionne le texte pour `locale`.
 * Renvoie '' si le contenu est illisible.
 */
export function pickCategoryName(
  raw: string | LocalizedText | null | undefined,
  locale: string,
): string {
  return pickLocalizedText(parseCategoryName(raw), locale);
}
